import type { HelpGuide } from '../types';

export const reclassGuide: HelpGuide = {
  key: 'reclass',
  title: 'รายการจัดประเภท (Reclassification)',
  subtitle: 'เมนู: รายงานและภาษี ▸ รายการจัดประเภท',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/reclass/คู่มือการใช้งาน_รายการจัดประเภท_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “รายการจัดประเภท (Reclassification)” ใช้ทำใบสำคัญจัดประเภทรายการ ณ วันสิ้นงวด (Period-end JV) เช่น ย้ายเงินกู้ระยะยาวส่วนที่ครบกำหนดใน 12 เดือนข้างหน้าไปเป็นหนี้สินหมุนเวียน หรือจัดประเภทรายการตามที่ผู้สอบบัญชี (Auditor) แนะนำ การบันทึกแบบ Draft จะยังไม่สร้าง JV — ระบบจะสร้างใบสำคัญ (JV) จริงในบัญชีแยกประเภท Dr บัญชีปลายทาง (To) / Cr บัญชีต้นทาง (From) เมื่อกด Post JV (ทันทีตอนบันทึก หรือกด Post ภายหลัง)',
        },
        {
          kind: 'callout',
          text: 'หลักการ Reclassification: ลงบัญชีจริงใน GL (Dr To / Cr From) · JV เคารพ Balance Dr = Cr เสมอ · ตั้งค่า Auto-reverse ได้ — เมื่อขึ้นงวดถัดไป (N+1) ระบบกลับรายการให้อัตโนมัติ · ทุกใบมี code RC-YYYY-NNNNNN ผูกกับเลข JV และมีสถานะ ตรวจสอบย้อนหลังได้',
        },
        { kind: 'para', text: 'หน้าจอมี 4 แท็บ:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'รายการ Reclass — ',
              text: 'ทะเบียนรายการจัดประเภททั้งหมด + ตัวกรองสถานะ + หมวด 8 ประเภท + KPI (แท็บเริ่มต้น)',
            },
            {
              lead: 'Cross-Check Dashboard — ',
              text: 'ตรวจ Dr = Cr ทุกรายการ และสรุปยอด In/Out แยกตาม Section (BS-Asset / BS-Liab / BS-Eq / PL)',
            },
            {
              lead: 'Impact on FS · Before/After — ',
              text: 'ผลกระทบต่องบการเงิน — ผลต่างสุทธิ (Net Diff) ต่อบัญชี',
            },
            {
              lead: 'Audit Trail — ',
              text: 'ประวัติการสร้าง / Post / Reverse ของทุกรายการ',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/reclass/01-overview.png',
          caption:
            'ภาพรวม — KPI (รายการในงวด/ยอดรวม/รอ Post/Posted), หลักการ Lifecycle, หมวด 8 ประเภท และตารางทะเบียนรายการ Reclass',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'การ์ดสรุปยอด (KPI) 4 ใบ:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'รายการในงวดนี้ — ',
              text: 'จำนวนรายการทั้งหมดในงวด พร้อมแยก Draft · Posted · Reversed',
            },
            { lead: 'ยอดรวม — ', text: 'ผลรวมจำนวนเงินทุกสถานะ (บาท)' },
            { lead: 'รอ Post — ', text: 'จำนวนรายการสถานะ Draft ที่ยังรอ Post JV' },
            {
              lead: 'Posted (มี JV) — ',
              text: 'จำนวนรายการที่ Post เป็น JV แล้ว พร้อมยอดเงินรวม',
            },
          ],
        },
        {
          kind: 'para',
          text: 'ใต้ KPI มีแผง “หลักการ — Reclassification Lifecycle” 4 ขั้นตอน (Real GL Posting · Dr = Cr Balanced · Auto-reverse N+1 · Audit-friendly) ปุ่มมุมขวาบน: เลือกงวด (งวด YYYYMM) · Reverse next period (กลับรายการทุกใบที่ Posted ไปงวดถัดไป) · + เพิ่มรายการ Reclass (ปุ่มสีหลัก)',
        },
        {
          kind: 'para',
          text: 'ในแท็บ รายการ Reclass มีแถบหมวด 8 ประเภท (Current portion of LT Loan · ลูกหนี้สงสัยจะสูญ · Asset Held for Sale · เงินมัดจำลูกค้า > 1 ปี · เงินลงทุน — เปลี่ยน Category · Provision Re-class · Reclass ระหว่างประเภทรายได้ · อื่นๆ ตามที่ Auditor แนะนำ) แต่ละหมวดแสดงจำนวนรายการ + ยอดเงิน + อ้างอิงมาตรฐาน (TAS/TFRS) ถัดมาเป็นแถบกรองสถานะ ทั้งหมด / Draft / Posted / Reversed + ช่องค้นหา code/รายละเอียด ตารางคอลัมน์: เลขที่ · งวด · หมวด · รายละเอียด · From → To · จำนวนเงิน · Auto-reverse · สถานะ · การกระทำ (ปุ่ม ดู)',
        },
      ],
    },
    {
      id: 'filter-draft',
      heading: '3. กรองรายการสถานะ Draft (รอ Post JV)',
      blocks: [
        {
          kind: 'para',
          text: 'กดแถบสถานะ Draft เพื่อดูเฉพาะรายการที่ยังเป็นร่าง — บันทึกไว้แล้วแต่ยังไม่ได้สร้าง JV จริง รายการเหล่านี้รอการ Post (ป้ายสถานะสีส้ม DRAFT) กดปุ่ม ดู ที่ท้ายแถวเพื่อเปิดรายละเอียดและ Post JV',
        },
        {
          kind: 'image',
          src: '/help/reclass/02-filter-draft.png',
          caption: 'กรองเฉพาะรายการสถานะ Draft — รายการที่รอ Post JV (ป้ายสีส้ม DRAFT)',
        },
      ],
    },
    {
      id: 'filter-posted',
      heading: '4. กรองรายการสถานะ Posted (มี JV แล้ว)',
      blocks: [
        {
          kind: 'para',
          text: 'กดแถบสถานะ Posted เพื่อดูเฉพาะรายการที่ Post เป็น JV จริงในบัญชีแยกประเภทแล้ว (ป้ายสถานะสีเขียว POSTED) แต่ละแถวแสดงวันที่ Post (postedAt) ใต้ป้ายสถานะ รายการที่ตั้ง Auto-reverse จะมีป้าย N+1 — ระบบจะกลับรายการให้เมื่อขึ้นงวดถัดไป',
        },
        {
          kind: 'image',
          src: '/help/reclass/03-filter-posted.png',
          caption: 'กรองเฉพาะรายการสถานะ Posted — มี JV แล้ว (ป้ายสีเขียว POSTED + วันที่ Post)',
        },
      ],
    },
    {
      id: 'add',
      heading: '5. การสร้างรายการ Reclass ใหม่',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม + เพิ่มรายการ Reclass แล้วกรอก: หมวด (Category — 1 ใน 8 ประเภท) · รายละเอียด (เช่น ย้ายเงินกู้ระยะยาว 240M ที่ครบกำหนดใน 12 เดือนข้างหน้า → ระยะสั้น) · บัญชี From (Cr) — บัญชีต้นทาง · บัญชี To (Dr) — บัญชีปลายทาง (ต้องไม่ใช่บัญชีเดียวกัน) · จำนวนเงิน (บาท) · Auto-reverse งวดถัดไป (ใช่ = กลับรายการอัตโนมัติ / ไม่ = ถาวร)',
        },
        {
          kind: 'para',
          text: 'ระบบแสดง JV Preview ให้ดูก่อนบันทึก: Dr {บัญชี To} / Cr {บัญชี From} ตามจำนวนเงินที่กรอก จากนั้นเลือก บันทึก Draft (เก็บเป็นร่าง ยังไม่ลง JV) หรือ บันทึก + Post JV (บันทึกและลง JV จริงทันที)',
        },
        {
          kind: 'image',
          src: '/help/reclass/04-add-modal.png',
          caption:
            'ฟอร์มสร้างรายการ Reclass — หมวด/รายละเอียด/From-To/จำนวนเงิน/Auto-reverse และ JV Preview (Dr/Cr)',
        },
      ],
    },
    {
      id: 'detail-draft',
      heading: '6. รายละเอียดรายการ Draft + ปุ่ม Post JV',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม ดู ที่รายการสถานะ Draft จะเปิดหน้าต่างรายละเอียด แสดง code · หมวด · มาตรฐานอ้างอิง · งวด · รายละเอียด · ตาราง JV (Dr บัญชี To / Cr บัญชี From พร้อมยอดรวม) และกล่องแจ้งว่า “Draft · ยังไม่มี JV — กด Post เพื่อสร้าง JV จริง” กดปุ่ม Post JV เพื่อลงบัญชีจริง (สถานะเปลี่ยนเป็น Posted)',
        },
        {
          kind: 'image',
          src: '/help/reclass/05-detail-draft.png',
          caption: 'รายละเอียดรายการ Draft — ตาราง JV (Dr/Cr) และปุ่ม Post JV เพื่อสร้าง JV จริง',
        },
      ],
    },
    {
      id: 'detail-posted',
      heading: '7. รายละเอียดรายการที่ Post แล้ว',
      blocks: [
        {
          kind: 'para',
          text: 'รายการสถานะ Posted เมื่อกด ดู จะแสดงตาราง JV เหมือนเดิม พร้อมกล่องสีเขียว “JV posted: {เลข JV}…” ที่ผูกใบสำคัญจริงไว้ หากรายการถูกกลับรายการแล้ว จะแสดงเลข Reversal JV ต่อท้ายด้วย รายการที่ Post แล้วจะไม่มีปุ่ม Post JV อีก หากต้องการล้างยอด ให้ใช้การกลับรายการ (reverse) แทน',
        },
        {
          kind: 'image',
          src: '/help/reclass/06-detail-posted.png',
          caption: 'รายละเอียดรายการที่ Post แล้ว — กล่อง JV posted แสดงเลข JV ที่ผูกไว้',
        },
      ],
    },
    {
      id: 'crosscheck',
      heading: '8. Cross-Check Dashboard',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ Cross-Check Dashboard ตรวจความถูกต้องของรายการ 2 ส่วน: (1) “① Dr = Cr ทุกรายการ” — ตารางแสดงทุก code พร้อม Dr · Cr · ผลต่าง (= 0) · ผลลัพธ์ (✓ Pass) เพราะ JV ที่ post ผ่านระบบ journal-entries ผ่าน balance check อยู่แล้ว มีป้าย ✓ ผ่านทั้งหมด ที่หัวการ์ด (2) “② By-section rollup” — สรุปยอดแยกตาม Section (BS-Asset / BS-Liab / BS-Eq / PL) เป็น รวม In (Dr to) เทียบ รวม Out (Cr from)',
        },
        {
          kind: 'image',
          src: '/help/reclass/07-crosscheck.png',
          caption:
            'Cross-Check Dashboard — ตรวจ Dr = Cr ทุกรายการ และสรุปยอด In/Out แยกตาม Section',
        },
      ],
    },
    {
      id: 'impact',
      heading: '9. ผลกระทบต่องบการเงิน (Impact on FS)',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ Impact on FS · Before/After แสดง “เปรียบเทียบยอด — Net Diff ต่อบัญชี” โดยรวมเฉพาะรายการที่ POSTED + REVERSED ในงวดที่เลือก ตารางแสดง: บัญชี ({code} {ชื่อบัญชี}) · Diff (ผลต่างสุทธิ — สีเขียวถ้าเพิ่มขึ้น สีแดงถ้าลดลง) · มาจาก (code Reclass ต้นทาง) ใช้ดูว่าการจัดประเภทกระทบยอดบัญชีใดเท่าไร',
        },
        {
          kind: 'image',
          src: '/help/reclass/08-impact.png',
          caption: 'ผลกระทบต่องบการเงิน — Net Diff ต่อบัญชี (เพิ่ม/ลด) พร้อมที่มาของรายการ',
        },
      ],
    },
    {
      id: 'audit',
      heading: '10. Audit Trail (ประวัติการทำรายการ)',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ Audit Trail แสดง “ประวัติ Reclass ทั้งหมด” เรียงตามวันที่ล่าสุดก่อน ทุกเหตุการณ์ในวงจรชีวิตของรายการ ได้แก่ สร้าง (create) · Post · Reverse คอลัมน์: วันที่ · เลขที่ (code) · Action (ป้าย สร้าง/Post/Reverse) · ผู้กระทำ · หมายเหตุ ใช้ตรวจสอบย้อนหลังว่าใครทำอะไรเมื่อไร',
        },
        {
          kind: 'image',
          src: '/help/reclass/09-audit.png',
          caption:
            'Audit Trail — ประวัติการสร้าง / Post / Reverse ของทุกรายการ พร้อมผู้กระทำและเวลา',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '11. สรุปสถานะของรายการ',
      blocks: [
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย'],
          rows: [
            ['DRAFT', 'บันทึกเป็นร่างแล้ว แต่ยังไม่มี JV — รอ Post (ป้ายสีส้ม)'],
            ['POSTED', 'Post เป็น JV จริงในบัญชีแยกประเภทแล้ว (ป้ายสีเขียว)'],
            ['REVERSED', 'กลับรายการแล้ว (เช่น Auto-reverse งวดถัดไป) — ป้ายสีกลาง'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '12. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'Auto-reverse งวดถัดไป (N+1) คืออะไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'เมื่อตั้งค่า Auto-reverse = ใช่ ระบบจะกลับรายการ (Dr from / Cr to) ให้อัตโนมัติเมื่อขึ้นงวดถัดไป เหมาะกับการจัดประเภท ณ สิ้นงวดที่ต้องล้างกลับต้นงวดถัดไป หากตั้งเป็น ไม่ (permanent) รายการจะคงอยู่ถาวร',
            },
          ],
        },
        { kind: 'para', text: 'ปุ่ม Reverse next period ใช้ทำอะไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'กลับรายการทุกใบที่สถานะ Posted ในงวดปัจจุบันไปเป็น JV กลับรายการในงวดถัดไป (N+1) แบบเป็นชุด รายการที่กลับไปแล้วจะถูกข้าม (idempotent) ใช้ได้เมื่อมีรายการ Posted ในงวด',
            },
          ],
        },
        { kind: 'para', text: 'จะแก้ไขหรือยกเลิกรายการที่ Post แล้วได้อย่างไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'หน้านี้ไม่มีปุ่มแก้ไข/ลบรายการ การกระทำที่มีคือ ดู (เปิดรายละเอียด), Post JV (เฉพาะรายการ Draft) และการกลับรายการ (reverse) รายการ Draft กด ดู แล้วกด Post JV เพื่อสร้าง JV จริง ส่วนรายการที่ Post เป็น JV แล้วถือเป็น audit-trail ดูได้อย่างเดียว หากต้องการล้างยอดต้องใช้การกลับรายการ (Auto-reverse งวดถัดไป หรือปุ่ม Reverse next period) แทนการแก้ไข/ลบ',
            },
          ],
        },
        { kind: 'para', text: 'บันทึกไม่ได้ / ปุ่มกดไม่ขึ้น?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ต้องเลือกบริษัทก่อน และกรอกครบ: รายละเอียด · บัญชี From · บัญชี To (ต้องไม่ซ้ำกัน) · จำนวนเงินมากกว่า 0',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '13. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'Reclassification — ',
              text: 'การจัดประเภทรายการบัญชีใหม่ (ย้ายยอดจากบัญชีหนึ่งไปอีกบัญชีหนึ่ง) มักทำ ณ สิ้นงวด',
            },
            {
              lead: 'JV (Journal Voucher) — ',
              text: 'ใบสำคัญทั่วไปที่ระบบสร้างจริงในบัญชีแยกประเภท Dr To / Cr From',
            },
            {
              lead: 'From (Cr) / To (Dr) — ',
              text: 'บัญชีต้นทางที่เครดิต (From) และบัญชีปลายทางที่เดบิต (To)',
            },
            {
              lead: 'Auto-reverse (N+1) — ',
              text: 'การกลับรายการอัตโนมัติเมื่อขึ้นงวดถัดไป',
            },
            {
              lead: 'Draft / Posted / Reversed — ',
              text: 'สถานะ: ร่าง (ยังไม่มี JV) / Post เป็น JV แล้ว / กลับรายการแล้ว',
            },
            {
              lead: 'code RC-YYYY-NNNNNN — ',
              text: 'เลขอ้างอิงรายการ Reclass ที่ระบบออกให้ ผูกกับเลข JV',
            },
          ],
        },
      ],
    },
  ],
};
